import { useState, type CSSProperties, type FC, type ReactNode } from 'react';
import { V4 } from '../tokens';
import { REVERT_FEEDBACK, type RevertFeedbackReason } from '@utils/featureRollout/strings';

// Prompt de feedback al revertir (D1): tras volver a la versión anterior preguntamos —de forma
// OMITIBLE— por qué. Chips de motivo + texto libre opcional. NO bloquea el revert (este componente
// se muestra DESPUÉS de ejecutarlo). El envío es fire-and-forget. Copy en `featureRollout/strings.ts`.
// 100% inline styles V4.

export interface RevertFeedbackPromptProps {
  // Envía el motivo (y texto opcional). Lo cablea el consumidor a submitRevertFeedback del hook.
  onSubmit: (reason: RevertFeedbackReason, text: string) => void;
  // Cierra el prompt sin enviar (Omitir / X). Omitible = no se penaliza al comercio.
  onSkip: () => void;
}

const CloseIcon: ReactNode = (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    aria-hidden="true"
  >
    <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
  </svg>
);

const cardStyle: CSSProperties = {
  background: V4.white,
  border: `1px solid ${V4.grayLight}`,
  borderRadius: 12,
  padding: '16px 16px 14px',
  fontFamily: 'inherit',
  position: 'relative',
  boxShadow: '0 8px 24px rgba(37,55,70,.08)',
};

const chipBase: CSSProperties = {
  border: `1.5px solid ${V4.grayLight}`,
  background: V4.white,
  color: V4.grayUltraDark,
  borderRadius: 999,
  padding: '7px 13px',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: 12.5,
  fontWeight: 600,
  whiteSpace: 'nowrap',
};

const chipSelected: CSSProperties = {
  ...chipBase,
  border: `1.5px solid ${V4.secondary}`,
  background: V4.secondaryFaded,
  color: V4.secondaryDark,
  fontWeight: 700,
};

const textareaStyle: CSSProperties = {
  width: '100%',
  marginTop: 12,
  minHeight: 64,
  resize: 'vertical',
  border: `1px solid ${V4.grayLight}`,
  borderRadius: 8,
  padding: '9px 11px',
  fontFamily: 'inherit',
  fontSize: 13,
  color: V4.primary,
  boxSizing: 'border-box',
};

const submitButton = (enabled: boolean): CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: enabled ? V4.secondary : V4.grayLight,
  color: V4.white,
  border: 0,
  borderRadius: 8,
  padding: '9px 18px',
  cursor: enabled ? 'pointer' : 'not-allowed',
  fontFamily: 'inherit',
  fontSize: 13,
  fontWeight: 700,
});

const skipButton: CSSProperties = {
  background: 'transparent',
  color: V4.grayUltraDark,
  border: 0,
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: 12.5,
  fontWeight: 600,
  padding: '9px 10px',
};

const closeButton: CSSProperties = {
  position: 'absolute',
  top: 10,
  right: 10,
  width: 26,
  height: 26,
  borderRadius: 7,
  border: 0,
  background: V4.grayBlueLight,
  color: V4.grayUltraDark,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};

export const RevertFeedbackPrompt: FC<RevertFeedbackPromptProps> = ({ onSubmit, onSkip }) => {
  const [selected, setSelected] = useState<RevertFeedbackReason | null>(null);
  const [text, setText] = useState('');
  const [sent, setSent] = useState(false);

  // Tras enviar mostramos un agradecimiento breve y dejamos que el consumidor cierre (onSkip
  // tras un tick, o el usuario lo descarta). Mantener el componente simple: no auto-desmonta.
  const handleSubmit = () => {
    if (!selected) return;
    onSubmit(selected, text);
    setSent(true);
  };

  if (sent) {
    return (
      <div style={cardStyle} role="status">
        <button type="button" style={closeButton} aria-label={REVERT_FEEDBACK.closeAria} onClick={onSkip}>
          {CloseIcon}
        </button>
        <span style={{ display: 'block', fontSize: 13.5, fontWeight: 700, color: V4.primary }}>
          {REVERT_FEEDBACK.thanks}
        </span>
      </div>
    );
  }

  return (
    <div style={cardStyle} role="group" aria-label={REVERT_FEEDBACK.title}>
      <button type="button" style={closeButton} aria-label={REVERT_FEEDBACK.closeAria} onClick={onSkip}>
        {CloseIcon}
      </button>

      <span style={{ display: 'block', fontSize: 14, fontWeight: 800, color: V4.primary, paddingRight: 24 }}>
        {REVERT_FEEDBACK.title}
      </span>
      <span style={{ display: 'block', fontSize: 12, color: V4.grayUltraDark, marginTop: 3, lineHeight: 1.45 }}>
        {REVERT_FEEDBACK.subtitle}
      </span>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
        {REVERT_FEEDBACK.options.map((opt) => {
          const isOn = selected === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              aria-pressed={isOn}
              style={isOn ? chipSelected : chipBase}
              onClick={() => setSelected(opt.value)}
            >
              {opt.label}
            </button>
          );
        })}
      </div>

      <textarea
        style={textareaStyle}
        placeholder={REVERT_FEEDBACK.textPlaceholder}
        value={text}
        onChange={(e) => setText(e.target.value)}
      />

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6, marginTop: 10 }}>
        <button type="button" style={skipButton} onClick={onSkip}>
          {REVERT_FEEDBACK.skip}
        </button>
        <button type="button" style={submitButton(Boolean(selected))} onClick={handleSubmit} disabled={!selected}>
          {REVERT_FEEDBACK.submit}
        </button>
      </div>
    </div>
  );
};

export default RevertFeedbackPrompt;
